// ═══════════════════════════════════════════════════════════════════════════
// tutoriales.js — Librería cliente del Módulo Tutoriales (Bloque 2 — 26-may-2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Patrón clonado de `debugReports.js` (módulo Debug) y `aiFeedback` (módulo IA).
// Vive separado del módulo Comunicaciones porque tiene su propio ciclo de vida
// y catálogos.
//
// LO QUE EXPONE — resumen:
//
//   Subir un tutorial:
//     - subirTutorial(metadata, videoBlob, thumbnailBlob?, onProgress?)
//       Orquesta el flujo de 3 pasos (crear → upload → finalizar). Devuelve
//       { id, videoUrl, thumbnailUrl } cuando termina.
//
//   Lecturas en tiempo real:
//     - suscribirTutoriales(callback, filtros?)
//     - suscribirTutorial(id, callback)
//     - suscribirComentarios(id, callback)
//
//   Mutaciones de un tutorial:
//     - actualizarMetadata(id, { titulo, descripcion, modulo, tags })
//     - pausarTutorial(id) / republicarTutorial(id)
//     - eliminarTutorial(id) — destructivo, via callable
//
//   Interacción:
//     - registrarVista(id) — al cruzar 10s de reproducción (debounced)
//     - agregarComentario(id, texto)
//
// Las callables están en `functions/tutoriales.js` (Bloque 2 también).
// ═══════════════════════════════════════════════════════════════════════════

import {
  collection, doc, addDoc, updateDoc, deleteDoc, query, where, orderBy,
  limit as fbLimit, onSnapshot, serverTimestamp, getDoc, Timestamp,
} from 'firebase/firestore'
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL,
} from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions, auth } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { COLLECTIONS } from '@core/lib/constants'
import { forUpdate } from '@core/lib/dbHelpers'
import { notificar, NOTIFICACION_PRIORIDADES } from '@core/lib/notificaciones'
import { getOrCreateDirecta, enviarMensaje } from '@core/lib/chat'

// ─── Catálogos ────────────────────────────────────────────────────────────

export const ESTADOS_TUTORIAL = Object.freeze({
  SUBIENDO:  'subiendo',
  PUBLICADO: 'publicado',
  PAUSADO:   'pausado',
})

export const ESTADO_LABELS = Object.freeze({
  subiendo:  { label: 'Subiendo…',  color: 'amber',   orden: 1 },
  publicado: { label: 'Publicado',  color: 'emerald', orden: 2 },
  pausado:   { label: 'Pausado',    color: 'zinc',    orden: 3 },
})

// Enum cerrada de módulos — espejo de la FUENTE `functions/lib/tutorialesLimites.js`
// (módulo hoja del backend). El front no puede importar `functions/` en runtime,
// así que los códigos se repiten acá con su label; el candado es
// `tutoriales.paridad.test.js` (al lado de este archivo), que compara los códigos
// y los topes de abajo contra ese archivo y contra un ancla tipeada a mano.
// Si agregás un módulo nuevo: primero la fuente, después acá — el test avisa.
export const MODULOS_TUTORIAL = Object.freeze([
  { codigo: 'general',         label: 'General' },
  { codigo: 'mercadolibre',    label: 'Mercado Libre' },
  { codigo: 'gestion',         label: 'Gestión' },
  { codigo: 'ia',              label: 'IA' },
  { codigo: 'casos',           label: 'Casos' },
  { codigo: 'postventa',       label: 'Postventa' },
  { codigo: 'productos',       label: 'Productos' },
  { codigo: 'rentabilidad',    label: 'Rentabilidad' },
  { codigo: 'comunicaciones',  label: 'Comunicaciones' },
  { codigo: 'diagnostico',     label: 'Diagnóstico' },
  { codigo: 'plataforma',      label: 'Plataforma' },
  { codigo: 'consultoria',     label: 'Consultoría' },
  { codigo: 'portal',          label: 'Portal' },
  { codigo: 'asignaciones',    label: 'Asignaciones' },
  { codigo: 'tutoriales',      label: 'Tutoriales' },
])

// Constantes — espejo de `functions/lib/tutorialesLimites.js` (mismo candado de paridad).
export const DURACION_MAX_SEG = 5 * 60
export const TAMANO_MAX_BYTES = 250 * 1024 * 1024
// Topes de forma de lo que se manda al backend: los `maxLength` de los formularios
// (TutorialRecorder, CompartirTutorialModal) leen de acá, no llevan literales.
export const MAX_TITULO = 200
export const MAX_DESCRIPCION = 4000
export const MAX_TAGS = 10
export const MAX_TAG = 40
export const MAX_MENSAJE_PERSONAL = 1000

// ─── Callables (lazy — se crean al primer uso) ────────────────────────────

let _crearFn, _finalizarFn, _eliminarFn, _registrarVistaFn, _emailFn

function _crear()           { return _crearFn          ||= httpsCallable(functions, 'crearTutorial') }
function _finalizar()       { return _finalizarFn      ||= httpsCallable(functions, 'finalizarTutorial') }
function _eliminar()        { return _eliminarFn       ||= httpsCallable(functions, 'eliminarTutorial') }
function _registrarVistaFn_() { return _registrarVistaFn ||= httpsCallable(functions, 'registrarVistaTutorial') }
function _emailCallable()   { return _emailFn          ||= httpsCallable(functions, 'enviarTutorialPorEmail') }

// ═══════════════════════════════════════════════════════════════════════════
// Subir un tutorial — orquesta el flujo de 3 pasos
// ═══════════════════════════════════════════════════════════════════════════
//
// Pasos:
//   1. Callable crearTutorial(metadata) → server crea doc en `subiendo`, devuelve id.
//   2. Upload del video a `tutoriales/{id}/video.webm` con uploadBytesResumable.
//   3. (Opcional) Upload de thumbnail a `tutoriales/{id}/thumb.jpg`.
//   4. Callable finalizarTutorial({id, duracionSeg}) → server confirma upload,
//      genera signed URLs, pasa estado a `publicado`.
//
// Si falla en cualquier paso intermedio, el doc queda en estado `subiendo`. Lo
// borra, con su video, `limpiarTutorialesColgadosScheduled` pasado un día
// (functions/tutorialesLimpieza.js), si su interruptor está prendido; si no,
// queda hasta que alguien con `tutoriales.eliminar` lo borre. NO retry
// automático: el usuario reintenta a mano (es lo mejor para no acumular jobs zombi).
//
// @param {Object} metadata - { titulo, descripcion?, modulo?, tags? }
// @param {Blob} videoBlob - el .webm del MediaRecorder
// @param {Object} extras
// @param {Blob} [extras.thumbnailBlob] - JPEG capturado del primer frame
// @param {number} extras.duracionSeg - duración real del video (≤ 300)
// @param {function} [extras.onProgress] - (pct: 0-100) llamado durante el upload
// @returns {Promise<{id, videoUrl, thumbnailUrl, tamanoBytes}>}
export async function subirTutorial(metadata, videoBlob, extras = {}) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  if (!metadata?.titulo) throw new Error('titulo requerido')
  if (!(videoBlob instanceof Blob)) throw new Error('videoBlob debe ser un Blob')
  const duracionSeg = Number(extras.duracionSeg)
  if (!Number.isFinite(duracionSeg) || duracionSeg <= 0 || duracionSeg > DURACION_MAX_SEG) {
    throw new Error(`duracionSeg inválida (debe ser 0 < d ≤ ${DURACION_MAX_SEG})`)
  }
  if (videoBlob.size > TAMANO_MAX_BYTES) {
    throw new Error(`Video pesa más del máximo permitido (${Math.round(TAMANO_MAX_BYTES / 1024 / 1024)} MB)`)
  }

  // Paso 1 — crear doc server-side
  const crearResp = await _crear()({
    titulo: metadata.titulo,
    descripcion: metadata.descripcion || null,
    modulo: metadata.modulo || 'general',
    tags: metadata.tags || [],
  })
  const { id, videoPath } = crearResp.data
  if (!id) throw new Error('crearTutorial no devolvió id')

  // Paso 2 — upload del video a Storage (resumable con progress)
  await _uploadConProgreso(videoPath, videoBlob, 'video/webm', extras.onProgress)

  // Paso 2.5 — resolver downloadURL del video. Lo hace el cliente con
  // `getDownloadURL` del SDK de Firebase (NO requiere permisos extra) en
  // vez de que el server use `getSignedUrl()` del Admin SDK (que requiere
  // IAM `serviceAccountTokenCreator`). Es el mismo patrón que usa el módulo
  // Debug (debugReports.js).
  const videoUrl = await getDownloadURL(storageRef(storage, videoPath))

  // Paso 3 — thumbnail opcional. Si falla, seguimos sin ella.
  let thumbnailUrl = null
  if (extras.thumbnailBlob instanceof Blob) {
    try {
      const thumbPath = `tutoriales/${id}/thumb.jpg`
      await _uploadConProgreso(thumbPath, extras.thumbnailBlob, 'image/jpeg', null)
      thumbnailUrl = await getDownloadURL(storageRef(storage, thumbPath))
    } catch (err) {
      console.warn('[tutoriales] upload/url thumbnail falló (graceful):', err.message)
    }
  }

  // Paso 4 — finalizar server-side. El cliente le pasa videoUrl + thumbnailUrl
  // que ya resolvió; el server las valida (que apunten al path esperado) y
  // las guarda en el doc, pasando estado a publicado.
  const finalResp = await _finalizar()({
    id,
    duracionSeg: Math.round(duracionSeg),
    videoUrl,
    thumbnailUrl,
  })
  return {
    id,
    videoUrl: finalResp.data.videoUrl,
    thumbnailUrl: finalResp.data.thumbnailUrl,
    tamanoBytes: finalResp.data.tamanoBytes,
  }
}

// Helper de upload con progress.
function _uploadConProgreso(path, blob, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const ref = storageRef(storage, path)
    const task = uploadBytesResumable(ref, blob, { contentType })
    task.on('state_changed',
      (snap) => {
        if (typeof onProgress === 'function' && snap.totalBytes > 0) {
          onProgress((snap.bytesTransferred / snap.totalBytes) * 100)
        }
      },
      (err) => reject(err),
      () => resolve(),
    )
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// Lecturas — suscripciones en tiempo real
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Suscribe al listado de tutoriales.
 *
 * Por default solo trae `publicado` (los `pausado` y `subiendo` quedan ocultos
 * a usuarios sin permiso de eliminar). Si querés ver todos, pasá `{verTodos: true}`
 * en filtros — el caller debe asegurar que el usuario tiene `tutoriales.eliminar`.
 *
 * @param {Function} callback - (rows, err) => void
 * @param {Object} [filtros]
 * @param {boolean} [filtros.verTodos=false] - incluye pausados/subiendo
 * @param {string} [filtros.modulo] - filtra por módulo
 * @param {number} [filtros.limit=200]
 * @returns {Function} unsubscribe
 */
export function suscribirTutoriales(callback, filtros = {}) {
  const { verTodos = false, modulo = null, limit = 200 } = filtros
  const constraints = []
  if (!verTodos) constraints.push(where('estado', '==', ESTADOS_TUTORIAL.PUBLICADO))
  if (modulo)    constraints.push(where('modulo', '==', modulo))
  constraints.push(orderBy('creadoEn', 'desc'))
  constraints.push(fbLimit(limit))

  const q = query(collection(db, COLLECTIONS.TUTORIALES), ...constraints)
  return onSnapshot(q,
    (snap) => callback(snap.docs.map(docConId), null),
    (err) => { console.error('[tutoriales] suscripción error', err); callback([], err) },
  )
}

/** Suscribe al detalle de un tutorial. */
export function suscribirTutorial(id, callback) {
  if (!id) { callback(null, new Error('id requerido')); return () => {} }
  return onSnapshot(doc(db, COLLECTIONS.TUTORIALES, id),
    (snap) => callback(snap.exists() ? docConId(snap) : null, null),
    (err) => { console.error('[tutoriales] suscripción detalle error', err); callback(null, err) },
  )
}

/** Suscribe al hilo de comentarios de un tutorial. */
export function suscribirComentarios(id, callback) {
  if (!id) { callback([], new Error('id requerido')); return () => {} }
  const q = query(
    collection(db, COLLECTIONS.TUTORIALES, id, 'comentarios'),
    orderBy('creadoEn', 'asc'),
  )
  return onSnapshot(q,
    (snap) => callback(snap.docs.map(docConId), null),
    (err) => { console.error('[tutoriales] comentarios error', err); callback([], err) },
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Mutaciones — escrituras directas a Firestore (gateadas por reglas)
// ═══════════════════════════════════════════════════════════════════════════

const CAMPOS_METADATA_EDITABLES = new Set(['titulo', 'descripcion', 'modulo', 'tags'])

/** Actualiza metadata de un tutorial (titulo, descripcion, modulo, tags). */
export async function actualizarMetadata(id, patch) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  if (!id) throw new Error('id requerido')
  const limpio = {}
  for (const [k, v] of Object.entries(patch || {})) {
    if (CAMPOS_METADATA_EDITABLES.has(k)) limpio[k] = v
  }
  if (Object.keys(limpio).length === 0) return
  await updateDoc(doc(db, COLLECTIONS.TUTORIALES, id), forUpdate(limpio))
}

/** Pausa un tutorial — lo oculta a quienes NO tienen `tutoriales.eliminar`. */
export async function pausarTutorial(id) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  await updateDoc(doc(db, COLLECTIONS.TUTORIALES, id), forUpdate({ estado: ESTADOS_TUTORIAL.PAUSADO }))
}

/** Republica un tutorial pausado. */
export async function republicarTutorial(id) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  await updateDoc(doc(db, COLLECTIONS.TUTORIALES, id), forUpdate({ estado: ESTADOS_TUTORIAL.PUBLICADO }))
}

/** Elimina un tutorial destructivamente (server-side via callable). */
export async function eliminarTutorial(id) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  if (!id) throw new Error('id requerido')
  const resp = await _eliminar()({ id })
  return resp.data
}

// ═══════════════════════════════════════════════════════════════════════════
// Vistas y comentarios
// ═══════════════════════════════════════════════════════════════════════════

// Debounce para registrarVista — un user que se la mira varias veces no
// suma 1 cada vez que cruza 10s. Cooldown de 30 min por (uid, id).
const _vistasRegistradas = new Map()
const VISTA_COOLDOWN_MS = 30 * 60 * 1000

/**
 * Registra una vista al cruzar 10s de reproducción.
 * Con cooldown client-side de 30 min para no inflar el contador.
 *
 * @param {string} id - tutorialId
 * @returns {Promise<{ok, registrado}>}
 */
export async function registrarVista(id) {
  if (!auth.currentUser) return { ok: false, registrado: false }
  if (!id) return { ok: false, registrado: false }

  const key = `${auth.currentUser.uid}:${id}`
  const ultima = _vistasRegistradas.get(key)
  if (ultima && (Date.now() - ultima) < VISTA_COOLDOWN_MS) {
    return { ok: true, registrado: false } // cooldown — no contar
  }
  _vistasRegistradas.set(key, Date.now())

  try {
    const resp = await _registrarVistaFn_()({ id })
    return resp.data
  } catch (err) {
    console.warn('[tutoriales] registrarVista falló (no bloquea):', err.message)
    return { ok: false, registrado: false }
  }
}

/** Agrega un comentario al hilo del tutorial. */
export async function agregarComentario(id, texto) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  if (!id) throw new Error('id requerido')
  const limpio = (texto || '').trim()
  if (!limpio) throw new Error('texto requerido')
  await addDoc(collection(db, COLLECTIONS.TUTORIALES, id, 'comentarios'), {
    autorUid: auth.currentUser.uid,
    autorEmail: auth.currentUser.email || null,
    texto: limpio.slice(0, 4000),
    creadoEn: serverTimestamp(),
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// Compartir un tutorial — 3 canales (Bloque 4)
// ═══════════════════════════════════════════════════════════════════════════
//
// Orquesta la entrega del link del tutorial a 1+ destinatarios por 1, 2 o 3
// canales (los que el usuario tildó en el modal Compartir):
//   - canales.notif: notificación in-app (campana).
//   - canales.chat:  mensaje en mensajería interna Milbrands (empresaId: null
//                    del Bloque 3). Requiere permiso `mensajes.iniciar-interna`
//                    en quien comparte; el caller debe haber chequeado antes.
//   - canales.email: email al destinatario via callable + Firebase Email Extension.
//
// Best-effort: si uno de los canales falla, los otros siguen. Devuelve un
// reporte por destinatario y por canal con `ok: true|false, err?: string`.
//
// @param {Object} args
// @param {Object} args.tutorial - { id, titulo, descripcion } — para armar
//                                 textos. El backend valida server-side todo
//                                 lo crítico.
// @param {string[]} args.destinatariosUids
// @param {{notif?:boolean, chat?:boolean, email?:boolean}} args.canales
// @param {string} [args.mensajePersonal] - texto opcional del remitente
// @returns {Promise<{ok, reportes: Array<{uid, notif?, chat?, email?}>}>}
export async function compartirTutorial({ tutorial, destinatariosUids, canales, mensajePersonal }) {
  if (!auth.currentUser) throw new Error('Auth requerida')
  if (!tutorial?.id) throw new Error('tutorial.id requerido')
  const dests = Array.from(new Set((destinatariosUids || []).filter(Boolean)))
    .filter(uid => uid !== auth.currentUser.uid)  // no compartir conmigo mismo
  if (dests.length === 0) throw new Error('Seleccioná al menos un destinatario')

  const reportes = []
  const remitenteUid = auth.currentUser.uid
  const link = `/tutoriales/${tutorial.id}`

  for (const uid of dests) {
    const rep = { uid }

    // ─── Notificación in-app ───
    if (canales?.notif) {
      try {
        await notificar({
          userId: uid,
          tipo: 'tutorial_compartido',
          titulo: `Te compartieron un tutorial: ${tutorial.titulo}`,
          cuerpo: (mensajePersonal || '').slice(0, 240) || tutorial.descripcion?.slice(0, 240) || '',
          link,
          recursoTipo: 'tutorial',
          recursoId: tutorial.id,
          prioridad: NOTIFICACION_PRIORIDADES.NORMAL,
        })
        rep.notif = { ok: true }
      } catch (err) {
        console.warn('[compartirTutorial] notif falló para', uid, err)
        rep.notif = { ok: false, err: err.message }
      }
    }

    // ─── Mensajería interna Milbrands (Bloque 3) ───
    if (canales?.chat) {
      try {
        // Crear/encontrar conversación directa con empresaId: null
        const conv = await getOrCreateDirecta({
          empresaId: null,
          uidA: remitenteUid,
          uidB: uid,
          creadoPor: remitenteUid,
        })
        // Armar el texto del mensaje con el link
        const texto = [
          `📺 Tutorial: ${tutorial.titulo}`,
          tutorial.descripcion ? `\n${tutorial.descripcion}` : '',
          mensajePersonal ? `\n\n${mensajePersonal}` : '',
          `\n\nVer: ${window.location.origin}${link}`,
        ].filter(Boolean).join('')
        await enviarMensaje({
          conversacionId: conv.id,
          autorId: remitenteUid,
          autorNombre: auth.currentUser.displayName || auth.currentUser.email || '',
          texto,
        })
        rep.chat = { ok: true, conversacionId: conv.id }
      } catch (err) {
        console.warn('[compartirTutorial] chat falló para', uid, err)
        rep.chat = { ok: false, err: err.message }
      }
    }

    // ─── Email (via callable + Firebase Email Extension) ───
    if (canales?.email) {
      try {
        const resp = await _emailCallable()({
          tutorialId: tutorial.id,
          destinatarioUid: uid,
          mensajePersonal: mensajePersonal || null,
        })
        rep.email = { ok: true, mailDocId: resp.data?.mailDocId }
      } catch (err) {
        console.warn('[compartirTutorial] email falló para', uid, err)
        rep.email = { ok: false, err: err.message }
      }
    }

    reportes.push(rep)
  }

  return { ok: true, reportes }
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers de UI
// ═══════════════════════════════════════════════════════════════════════════

export function fmtFecha(ts) {
  if (!ts) return '—'
  const d = ts.toDate ? ts.toDate() : (ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts))
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function fmtDuracion(seg) {
  const s = Number(seg)
  if (!Number.isFinite(s) || s <= 0) return '—'
  const m = Math.floor(s / 60)
  const r = Math.round(s % 60)
  return `${m}:${String(r).padStart(2, '0')}`
}

export function fmtTamano(bytes) {
  const b = Number(bytes)
  if (!Number.isFinite(b) || b <= 0) return '—'
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}
